import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import { UserPlus, Trash2, AlertCircle, CheckCircle, Mail, User as UserIcon, Shield, Edit, Key, Power, X } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmDialog from './ConfirmDialog';

const UserManagement = () => {
  const { role } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user'
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    email: '',
    role: ''
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'danger'
  });

  useEffect(() => {
    if (role === 'admin') {
      fetchUsers();
    }
  }, [role, statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const statusParam = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await fetch(`${API_URL}/api/auth/users${statusParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(Object.entries(data).map(([username, userData]) => ({
        username,
        ...userData
      })));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newUser),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create user');
      }

      toast.success('User created successfully!');
      setNewUser({ username: '', email: '', password: '', role: 'user' });
      setShowAddForm(false);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteUser = (username) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete User',
      message: `Are you sure you want to delete user "${username}"? This action cannot be undone.`,
      type: 'danger',
      onConfirm: () => executeDeleteUser(username)
    });
  };

  const executeDeleteUser = async (username) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/users/${username}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete user');
      }

      toast.success('User deleted successfully!');
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user.username);
    setEditFormData({
      email: user.email,
      role: user.role
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/users/${editingUser}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editFormData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update user');
      }

      toast.success('User updated successfully!');
      setShowEditModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleUserStatus = (username, currentStatus) => {
    const action = currentStatus ? 'disable' : 'enable';
    setConfirmDialog({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
      message: `Are you sure you want to ${action} user "${username}"?`,
      type: 'warning',
      onConfirm: () => executeToggleUserStatus(username, currentStatus)
    });
  };

  const executeToggleUserStatus = async (username, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/users/${username}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to toggle user status');
      }

      const data = await response.json();
      toast.success(data.message);
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResetPassword = (username) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Send Password Reset Email',
      message: `Send password reset email to "${username}"?`,
      type: 'info',
      onConfirm: () => executeSendPasswordReset(username)
    });
  };

  const executeSendPasswordReset = async (username) => {
    try{
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/users/${username}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send reset email');
      }

      const data = await response.json();
      toast.success(data.message);
    } catch (err) {
      setError(err.message);
    }
  };

  if (role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-screen bg-mongodb-gray-light2">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-mongodb-red-base mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-mongodb-gray-dark3 mb-2">Access Denied</h2>
          <p className="text-mongodb-gray-dark1">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-mongodb-gray-dark3 mb-2">User Management</h1>
        <p className="text-mongodb-gray-dark1">Manage user accounts and permissions</p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-mongodb-red-light3 border border-mongodb-red-light2 rounded-lg flex items-center gap-2 text-mongodb-red-dark2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Filter and Add User Button */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-mongodb-gray-dark2">Filter:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-mongodb-gray-light1 rounded-lg focus:ring-2 focus:ring-mongodb-green-dark2 focus:border-mongodb-green-dark2"
          >
            <option value="all">All Users</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 px-4 py-2 bg-mongodb-green-dark2 text-white rounded-lg hover:bg-mongodb-green-dark1 transition-colors"
        >
          <UserPlus className="w-5 h-5" />
          {showAddForm ? 'Cancel' : 'Add New User'}
        </button>
      </div>

      {/* Add User Form */}
      {showAddForm && (
        <div className="mb-6 p-6 bg-white rounded-lg shadow-md border border-mongodb-gray-light2">
          <h2 className="text-xl font-bold text-mongodb-gray-dark3 mb-4">Create New User</h2>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-mongodb-gray-dark2 mb-1">
                  Username *
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-mongodb-gray-base" />
                  <input
                    type="text"
                    required
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-mongodb-gray-light1 rounded-lg focus:ring-2 focus:ring-mongodb-green-dark2 focus:border-mongodb-green-dark2"
                    placeholder="Enter username"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-mongodb-gray-dark2 mb-1">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-mongodb-gray-base" />
                  <input
                    type="email"
                    required
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-mongodb-gray-light1 rounded-lg focus:ring-2 focus:ring-mongodb-green-dark2 focus:border-mongodb-green-dark2"
                    placeholder="Enter email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-mongodb-gray-dark2 mb-1">
                  Password *
                </label>
                <input
                  type="password"
                  required
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="w-full px-4 py-2 border border-mongodb-gray-light1 rounded-lg focus:ring-2 focus:ring-mongodb-green-dark2 focus:border-mongodb-green-dark2"
                  placeholder="Enter password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-mongodb-gray-dark2 mb-1">
                  Role *
                </label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-mongodb-gray-base" />
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-mongodb-gray-light1 rounded-lg focus:ring-2 focus:ring-mongodb-green-dark2 focus:border-mongodb-green-dark2"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-mongodb-green-dark2 text-white rounded-lg hover:bg-mongodb-green-dark1 transition-colors"
              >
                Create User
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 bg-mongodb-gray-light2 text-mongodb-gray-dark2 rounded-lg hover:bg-mongodb-gray-light1 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-mongodb-gray-dark3">Edit User: {editingUser}</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingUser(null);
                  }}
                  className="text-mongodb-gray-base hover:text-mongodb-gray-dark2 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-mongodb-gray-dark2 mb-1">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-mongodb-gray-base" />
                    <input
                      type="email"
                      required
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-mongodb-gray-light1 rounded-lg focus:ring-2 focus:ring-mongodb-green-dark2 focus:border-mongodb-green-dark2"
                      placeholder="Enter email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-mongodb-gray-dark2 mb-1">
                    Role *
                  </label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-mongodb-gray-base" />
                    <select
                      value={editFormData.role}
                      onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-mongodb-gray-light1 rounded-lg focus:ring-2 focus:ring-mongodb-green-dark2 focus:border-mongodb-green-dark2"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-mongodb-green-dark2 text-white rounded-lg hover:bg-mongodb-green-dark1 transition-colors"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingUser(null);
                    }}
                    className="flex-1 px-4 py-2 bg-mongodb-gray-light2 text-mongodb-gray-dark2 rounded-lg hover:bg-mongodb-gray-light1 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-md border border-mongodb-gray-light2 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-mongodb-gray-light3 border-b border-mongodb-gray-light2">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-mongodb-gray-dark1 uppercase tracking-wider">
                  Username
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-mongodb-gray-dark1 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-mongodb-gray-dark1 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-mongodb-gray-dark1 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-mongodb-gray-dark1 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-mongodb-gray-dark1 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-mongodb-gray-light2">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-mongodb-gray-dark1">
                    Loading users...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-mongodb-gray-dark1">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => {
                  const isActive = user.is_active !== false;
                  const lastLogin = user.last_login ? new Date(user.last_login).toLocaleString() : 'Never';
                  
                  return (
                    <tr key={user.username} className={`hover:bg-mongodb-gray-light3 ${!isActive ? 'bg-mongodb-gray-light2' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 ${
                            isActive ? 'bg-mongodb-green-dark2' : 'bg-mongodb-gray-base'
                          }`}>
                            {user.username.charAt(0).toUpperCase()}
                          </div>
                          <span className={`font-medium ${isActive ? 'text-mongodb-gray-dark3' : 'text-mongodb-gray-dark1'}`}>
                            {user.username}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-mongodb-gray-dark2">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          user.role === 'admin' 
                            ? 'bg-mongodb-purple-light3 text-mongodb-purple-dark2' 
                            : 'bg-mongodb-green-light3 text-mongodb-green-dark2'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          isActive
                            ? 'bg-mongodb-green-light3 text-mongodb-green-dark2'
                            : 'bg-mongodb-red-light3 text-mongodb-red-dark2'
                        }`}>
                          {isActive ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-mongodb-gray-dark2 text-sm">
                        {lastLogin}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-mongodb-blue-base hover:text-mongodb-blue-dark1 transition-colors"
                            title="Edit user"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleResetPassword(user.username)}
                            className="text-mongodb-purple-base hover:text-mongodb-purple-dark2 transition-colors"
                            title="Send password reset email"
                          >
                            <Key className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleToggleUserStatus(user.username, isActive)}
                            className={`transition-colors ${
                              isActive 
                                ? 'text-mongodb-yellow-dark2 hover:text-mongodb-yellow-dark3' 
                                : 'text-mongodb-green-dark1 hover:text-mongodb-green-dark2'
                            }`}
                            title={isActive ? 'Disable user' : 'Enable user'}
                          >
                            <Power className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.username)}
                            className="text-mongodb-red-base hover:text-mongodb-red-dark2 transition-colors"
                            title="Delete user"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
      />
    </div>
  );
};

export default UserManagement;

